import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ScanFace, Upload, X, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PhotoGrid } from "@/components/PhotoCard";
import { PageHeader } from "@/components/Common";
import { CameraCapture } from "@/components/CameraCapture";
import { BundleOffers } from "@/components/BundleOffers";

export default function FaceSearch() {
  const { user, refreshMe, setUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const eventId = params.get("event");
  const inputRef = useRef();
  const [selfie, setSelfie] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [searchCount, setSearchCount] = useState(0);

  useEffect(() => {
    if (!user?.has_face) return;
    api.get("/face/matches", { params: eventId ? { event_id: eventId } : {} }).then((r) => setResults(r.data));
  }, [user?.has_face, eventId]);

  const toggleNotify = async (v) => {
    try {
      setUser((await api.put("/me/profile", { notify_new_photos: v })).data);
      toast.success(v ? "Pemberitahuan email aktif" : "Pemberitahuan email dimatikan");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const onFile = async (file) => {
    if (!file) return;
    setSelfie(URL.createObjectURL(file));
    setScanning(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/face/search", fd, { params: eventId ? { event_id: eventId } : {} });
      setResults(data);
      setSearchCount((c) => c + 1);      toast.success(`${data.length} foto cocok ditemukan`);
      refreshMe();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setScanning(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <PageHeader label="Pengenalan wajah" title="Cari Foto Wajahmu" />
      {eventId && (
        <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-sm" data-testid="face-event-filter">
          Hanya di event terpilih
          <button onClick={() => setParams({})} data-testid="face-event-filter-clear"><X className="h-4 w-4" /></button>
        </div>
      )}
      <div className="grid lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-5">
          <button onClick={() => inputRef.current.click()} disabled={scanning} data-testid="face-upload-dropzone"
            className="relative w-full aspect-square max-w-md rounded-3xl border-2 border-dashed border-zinc-300 bg-white hover:border-blue-400 hover:bg-blue-50/30 transition-colors overflow-hidden grid place-items-center">
            {selfie ? (
              <img src={selfie} alt="Swafoto" className="absolute inset-0 w-full h-full object-cover" data-testid="face-selfie-preview" />
            ) : (
              <div className="text-center px-8">
                <span className="mx-auto h-14 w-14 rounded-2xl bg-blue-50 text-blue-600 grid place-items-center mb-4"><ScanFace className="h-7 w-7" /></span>
                <p className="font-bold text-zinc-900">Unggah swafoto</p>
                <p className="text-sm text-zinc-500 mt-1">Wajah menghadap kamera, cahaya cukup, tanpa kacamata hitam.</p>
              </div>
            )}
            {scanning && (
              <div className="absolute inset-[18%]" data-testid="face-scanning-indicator">
                <span className="corner tl" /><span className="corner tr" /><span className="corner bl" /><span className="corner br" />
                <div className="scan-line" />
              </div>
            )}
          </button>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="user" className="hidden" onChange={(e) => onFile(e.target.files[0])} data-testid="face-upload-input" />
        </div>
        <div className="lg:col-span-7 flex flex-col justify-center">
          <h2 className="text-2xl font-extrabold tracking-tight mb-3">{scanning ? "Memindai wajah..." : "Satu swafoto, semua fotomu."}</h2>
          <p className="text-zinc-600 leading-relaxed max-w-lg">Kami mengubah wajahmu menjadi sidik wajah digital lalu membandingkannya dengan setiap wajah di foto event. Sidik wajah disimpan agar kamu bisa melihat foto baru tanpa unggah ulang.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={() => setCameraOpen(true)} disabled={scanning} className="rounded-xl bg-blue-600 hover:bg-blue-700 h-11" data-testid="face-camera-button">
              <Camera className="mr-2 h-4 w-4" />Ambil swafoto langsung
            </Button>
            <Button onClick={() => inputRef.current.click()} disabled={scanning} variant="outline" className="rounded-xl h-11 bg-white" data-testid="face-upload-button">
              <Upload className="mr-2 h-4 w-4" />{user?.has_face ? "Ganti dari file" : "Unggah dari file"}
            </Button>
          </div>
          <CameraCapture open={cameraOpen} onOpenChange={setCameraOpen} onCapture={onFile} />
          {user?.has_face && (
            <label className="mt-6 flex items-center gap-3 text-sm text-zinc-600 cursor-pointer w-fit">
              <Switch checked={user.notify_new_photos !== false} onCheckedChange={toggleNotify} data-testid="face-notify-switch" />
              Kabari saya lewat email saat ada foto baru yang cocok
            </label>
          )}
        </div>
      </div>
      {results && (
        <section data-testid="face-results">
          <BundleOffers eventId={eventId} refreshKey={searchCount} />
          <h3 className="text-lg font-bold mb-6"><span data-testid="face-results-count">{results.length}</span> foto cocok dengan wajahmu</h3>
          <PhotoGrid photos={results} empty="Belum ada foto yang cocok. Coba swafoto lain atau cek lagi nanti." />
        </section>
      )}
    </main>
  );
}
